/**
 * Pure H OS · Sprint 12 · Data store + live view proxies.
 *
 * The frontend fetches every collection from the backend at boot
 * (`api.bootstrap()`) and pipes the result through `hydrateAll(data)`.
 * From that moment on the `mocks/*.ts` bindings — which are the ONLY
 * data surface consumed by pages — see the real MongoDB rows.
 *
 * Why proxies and not React state? Because the ~76 page files import
 * these bindings at the module top level (`import { PRODUCTS } from
 * "../../mocks/commerceData"`). Rewriting every page to use a hook
 * would balloon the UI diff — the user's brief is explicitly "keep the
 * UI completely unchanged." The proxies preserve the exact same public
 * shape (an `Array<T>` you can `.map`, `.filter`, `.length`, spread,
 * destructure, or iterate) while under the hood forwarding every read
 * to the live cache.
 *
 * Re-render safety: pages render only after the auth context flips
 * `dataReady` to `true`, i.e. after `hydrateAll` has run. Any page that
 * later re-renders — because context, route, or local state changed —
 * reads the current cache through the same proxy. There is no stale
 * capture because arrays are not copied; the proxy target is a thin
 * shell that delegates every operation to the cache entry.
 */

// Raw collection cache. Keys match backend collection names (snake_case).
const CACHE: Record<string, unknown[]> = {};

/**
 * Replace the cache with `data` coming from `GET /api/pureh/data`.
 * Idempotent — safe to call again on session refresh.
 */
export function hydrateAll(data: Record<string, unknown[]>): void {
  for (const k of Object.keys(CACHE)) delete CACHE[k];
  for (const [k, v] of Object.entries(data)) CACHE[k] = Array.isArray(v) ? v : [];
}

/** Number of collections currently loaded. Used by health probes. */
export function loadedCollections(): number {
  return Object.keys(CACHE).length;
}

/** Direct read (typed). Prefer `liveArray` for consumer bindings. */
export function readCollection<T>(key: string): T[] {
  return (CACHE[key] as T[] | undefined) ?? [];
}

/**
 * A live array view over `CACHE[key]`. Every array operation (`length`,
 * indexed access, `.map`, `.filter`, `.find`, `[Symbol.iterator]`, …)
 * transparently reads the current cache entry.
 */
export function liveArray<T>(key: string): T[] {
  const empty: T[] = [];
  return new Proxy(empty, {
    get(_target, prop, receiver) {
      const src = (CACHE[key] as T[] | undefined) ?? empty;
      const v = Reflect.get(src, prop, receiver);
      return typeof v === "function" ? (v as (...args: unknown[]) => unknown).bind(src) : v;
    },
    has(_target, prop) {
      const src = (CACHE[key] as T[] | undefined) ?? empty;
      return prop in src;
    },
    ownKeys(_target) {
      const src = (CACHE[key] as T[] | undefined) ?? empty;
      return Reflect.ownKeys(src);
    },
    getOwnPropertyDescriptor(_target, prop) {
      const src = (CACHE[key] as T[] | undefined) ?? empty;
      return Object.getOwnPropertyDescriptor(src, prop);
    },
  });
}

/**
 * Mutate a row inside the local cache — used by the UI immediately
 * after a PATCH succeeds so the drawer reflects the new state without
 * waiting for a full bootstrap round-trip. If the row doesn't exist,
 * it is inserted (used by `rec_states` which is upsert-by-id server-side).
 * A `mutationTick` counter is bumped so React components can subscribe.
 */
let _mutationTick = 0;
const _mutationListeners = new Set<() => void>();
export function subscribeMutations(fn: () => void): () => void {
  _mutationListeners.add(fn);
  return () => { _mutationListeners.delete(fn); };
}
export function mutationTick(): number { return _mutationTick; }

export function upsertLocal<T extends { id?: string }>(collection: string, row: T): void {
  const arr = (CACHE[collection] as T[] | undefined);
  if (!arr) {
    CACHE[collection] = [row];
  } else {
    const idx = row.id ? arr.findIndex((r) => (r as { id?: string }).id === row.id) : -1;
    if (idx >= 0) arr[idx] = { ...arr[idx], ...row };
    else arr.push(row);
  }
  _mutationTick += 1;
  _mutationListeners.forEach((f) => { try { f(); } catch { /* ignore */ } });
}

/**
 * A live singleton view: reads `CACHE[key][0]` and returns its property
 * for any access. `fallback` is used before hydration finishes.
 */
export function liveSingleton<T extends object>(key: string, fallback: T): T {
  return new Proxy({} as T, {
    get(_target, prop) {
      const arr = (CACHE[key] as T[] | undefined) ?? [];
      const src = arr.length > 0 ? arr[0] : fallback;
      const v = (src as Record<string | symbol, unknown>)[prop as string | symbol];
      return typeof v === "function"
        ? (v as (...args: unknown[]) => unknown).bind(src)
        : v;
    },
    has(_target, prop) {
      const arr = (CACHE[key] as T[] | undefined) ?? [];
      const src = arr.length > 0 ? arr[0] : fallback;
      return prop in (src as object);
    },
    ownKeys(_target) {
      const arr = (CACHE[key] as T[] | undefined) ?? [];
      const src = arr.length > 0 ? arr[0] : fallback;
      return Reflect.ownKeys(src as object);
    },
    getOwnPropertyDescriptor(_target, prop) {
      const arr = (CACHE[key] as T[] | undefined) ?? [];
      const src = arr.length > 0 ? arr[0] : fallback;
      return Object.getOwnPropertyDescriptor(src as object, prop);
    },
  });
}
